import { Loader } from '@/components/custom-components/Loader'
import { Button } from '@/components/shadcn/button.tsx'
import { DialogTitle } from '@/components/shadcn/dialog.tsx'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/shadcn/form.tsx'
import { Input } from '@/components/shadcn/input.tsx'
import { Label } from '@/components/shadcn/label.tsx'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select.tsx'
import useMonitorSubscription from '@/hooks/useMonitorSubscription'
import showToastNotification from '@/hooks/useShowToast.tsx'
import { cn } from '@/lib/utils.ts'
import countriesJson from '@/utils/data/country-state.json'
import { TEAMS_EXTRA_SEAT_PRICE, getCurrencySymbol } from '@/utils/data/plans.ts'
import { trpc } from '@/utils/trpc.ts'
import { CardCVV, CardComponent, CardExpiry, CardNumber } from '@chargebee/chargebee-js-react-wrapper'
import ChargebeeComponents from '@chargebee/chargebee-js-react-wrapper/dist/components/ComponentGroup'
import { zodResolver } from '@hookform/resolvers/zod'
import { Lock, XCircle } from '@mynaui/icons-react'
import { useNavigate } from '@tanstack/react-router'
import { TRPCError } from '@trpc/server'
import { cva } from 'class-variance-authority'
import { SyntheticEvent, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { PaymentSourceDetails } from './PaymentSourceDetails'

export const nextDueDate = new Date(new Date().setDate(new Date().getDate() + 3)).toLocaleDateString('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
})

export const firstChargeDate = new Date(new Date().setDate(new Date().getDate())).toLocaleDateString('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
})

// Interface for a single state
export interface State {
  code: string // State code (e.g., "BDS", "BGL")
  name: string // Name of the state
  subdivision: string | string[] | null // Subdivision, nullable
}

// Interface for a single country
export interface Country {
  code2: string // Two-letter country code (e.g., "AF")
  code3: string // Three-letter country code (e.g., "AFG")
  name: string // Name of the country (e.g., "Afghanistan")
  capital: string // Capital city (e.g., "Kabul")
  region: string // Region (e.g., "Asia")
  subregion: string // Subregion (e.g., "Southern Asia")
  states: State[] // Array of states
}

// Preprocess data into a lookup map
export const countriesByCode2 = new Map<string, Country>()
countriesJson.forEach((country) => {
  countriesByCode2.set(country.code2, country)
})

// Get states by country code
export const getStatesByCode2 = (code2: string) => {
  const country = countriesByCode2.get(code2)
  return country ? country.states : []
}

// Get all countries with just `name` and `code2`
export const getAllCountries = () => {
  return Array.from(countriesByCode2.values())
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(({ name, code2 }) => ({
      name,
      code2,
    }))
}

export const capitalizeCardType = (str: string) => str.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())

export const inputStyles = cva(
  'mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 pt-2 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
)

export type AppliedPromoDetail = {
  id: string
  name: string
  discountType: string
  discountPercentage?: number
  status: string
  period?: number
  periodUnit?: string
  discountAmount?: number
  durationType: string
  currencyCode?: string
}

export const PurchaseTeamSeat = ({
  newSeatCount,
}: {
  newSeatCount: number
}) => {
  const cardRef = useRef<ChargebeeComponents | null>(null)

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const [addPromoOpen, setAddPromoOpen] = useState<boolean>(false)

  const [addCardOpen, setAddCardOpen] = useState<boolean>(false)

  const [upgradeSuccessShow, setUpgradeSuccessShow] = useState<boolean>(false)

  const [appliedPromo, setAppliedPromo] = useState<AppliedPromoDetail | undefined>(undefined)

  const [period] = useState<'monthly' | 'annually'>('monthly')

  const navigate = useNavigate()

  const defaultPrice = newSeatCount * TEAMS_EXTRA_SEAT_PRICE

  const countries = getAllCountries()

  const [checkoutSuccessful, setCheckoutSuccessful] = useState<boolean>(false)

  const { subscriptionSuccessful, isLoading: isMonitoringSubscription } = useMonitorSubscription(checkoutSuccessful)

  const { data: userPaymentDetails, isLoading: isLoadingPaymentDetails } =
    trpc.getUserPrimaryPaymentSource.useQuery(undefined)

  const checkoutFormSchema = z.object({
    chargebeeToken: z.string({ required_error: 'Chargebee Token is required' }),
    coupon: z.string(),
    plan: z.string({ required_error: 'Plan ID is required' }),
    firstName: z.string({ required_error: 'First name is required' }).min(2),
    lastName: z.string({ required_error: 'Last name is required' }).min(2),
    billingAddress: z.object({
      country:
        !addCardOpen && userPaymentDetails
          ? z.string().optional()
          : z
              .string({ required_error: 'Country is required' })
              .describe('country in ISO Code https://www.iso.org/iso-3166-country-codes.html')
              .min(2),
      zip:
        !addCardOpen && userPaymentDetails
          ? z.string().optional()
          : z.string({ required_error: 'Zip code is required' }).min(2),
      city:
        !addCardOpen && userPaymentDetails
          ? z.string().optional()
          : z.string({ required_error: 'City is required' }).min(2),
      state:
        !addCardOpen && userPaymentDetails
          ? z.string().optional()
          : z.string({ required_error: 'State/Region is required' }).min(2),
      line1:
        !addCardOpen && userPaymentDetails
          ? z.string().optional()
          : z.string({ required_error: 'Line 1 is required' }).min(2),
      line2: z.string().optional(),
    }),
  })

  const { mutate: openPortalSessionUrl, isPending: fetchingPortalSessionUrl } = trpc.getPortalSessionUrl.useMutation({
    onSuccess: async (data) => {
      if (data) {
        window.location.href = data
      }
    },
  })

  type CheckoutFormValues = z.infer<typeof checkoutFormSchema>

  const defaultValues: Partial<CheckoutFormValues> = {
    coupon: '',
    chargebeeToken: '',
    plan: '',
  }

  const form = useForm<CheckoutFormValues>({
    resolver: zodResolver(checkoutFormSchema),
    defaultValues,
  })

  const purchaseSeat = async () => {
    try {
      if (isPending) return
      purchaseSeatMutation({
        quantity: newSeatCount,
        // coupons: form.getValues('coupon') ? ([form.getValues('coupon')].filter(Boolean) as string[]) : undefined,
        chargebeeToken: form.getValues('chargebeeToken'),
        billingAdrress: form.getValues('billingAddress'),
      })
    } catch (e) {
      setIsSubmitting(false)
      if (e instanceof Error) {
        if (e.message.includes('already present')) {
          toast.error('This account already exists.')
          setTimeout(() => {
            navigate({ to: '/feeds/templates' })
          }, 2000)
        } else {
          toast.error(e.message) // Show the original error for other cases
        }
      } else {
        toast.error('An unknown error occurred')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // Initiate purchase seat subscription based on availability of card
  const subscribeToPlan = () => {
    if (userPaymentDetails && !addCardOpen) {
      purchaseSeat()
    } else if (cardRef.current) {
      cardRef.current
        .tokenize({
          addressLine1: form.getValues('billingAddress.line1'),
          addressLine2: form.getValues('billingAddress.line2'),
          city: form.getValues('billingAddress.city'),
          firstName: form.getValues('firstName'),
          lastName: form.getValues('lastName'),
          state: form.getValues('billingAddress.state'),
          zip: form.getValues('billingAddress.zip'),
          countryCode: form.getValues('billingAddress.country'),
        })
        .then((data: { token: string }) => {
          form.setValue('chargebeeToken', data.token)
        })
        .then(() => {
          setTimeout(purchaseSeat, 500)
        })
        .catch((e: TRPCError) => {
          setIsSubmitting(false)
          toast.error(e.message)
        })
        .finally(() => {
          setIsSubmitting(false)
        })
    } else {
      setIsSubmitting(false)
    }
  }

  const onSubmit = () => {
    setIsSubmitting(true)
    if (form.getValues('coupon') && form.getValues('coupon')?.length > 0 && !appliedPromo) {
      try {
        // We test the coupon first to confirm it works before procedding to create subscription
        return refetchCoupon({ throwOnError: true })
          .then(() => {
            // updateDueAmount(data);

            subscribeToPlan()
          })
          .catch((e) => {
            if (e instanceof Error) {
              if (e.message === "Sorry, we couldn't find that resource") toast.error('This coupon code is invalid!')
              else toast.error(e.message)
            } else {
              toast.error('An unknown error occurred')
            }
          })
      } catch (e) {
        setIsSubmitting(false)
        if (e instanceof Error) {
          if (e.message === "Sorry, we couldn't find that resource") toast.error('This coupon code is invalid!')
          else toast.error(e.message)
        } else {
          toast.error('An unknown error occurred')
        }
      } finally {
        setIsSubmitting(false)
      }
    } else {
      subscribeToPlan()
    }
  }

  const applyPromo = async () => {
    if (form.getValues('coupon') && form.getValues('coupon')?.length > 0) {
      try {
        const response = await refetchCoupon({ throwOnError: true })
        if (response && response.data) {
          // updateDueAmount(response.data);
          setAddPromoOpen(false)
          setAppliedPromo(response.data)
        }
      } catch (e) {
        if (e instanceof Error) {
          if (e.message === "Sorry, we couldn't find that resource") {
            form.setValue('coupon', '')
            toast.error('This coupon code is invalid!')
          } else toast.error(e.message)
        } else {
          toast.error('An unknown error occurred')
        }
      }
    }
  }

  const onReady = (event: SyntheticEvent) => {
    const element = event.currentTarget as HTMLElement
    if (element) element.focus()
  }

  const { isPending, mutate: purchaseSeatMutation } = trpc.purchaseSeat.useMutation({
    onSuccess: (response) => {
      if (response && response.id) {
        setCheckoutSuccessful(true)
      } else {
        setIsSubmitting(false)
      }
    },
    onError: (error) => {
      setIsSubmitting(false)
      if (error instanceof Error) {
        if (error.message.includes('already present')) {
          toast.error('This account already exists.')
          setTimeout(() => {
            navigate({ to: '/feeds/templates' })
          }, 2000)
        } else {
          toast.error(error.message) // Show the original error for other cases
        }
      } else {
        toast.error('An unknown error occurred')
      }
    },
  })

  useEffect(() => {
    if (subscriptionSuccessful) {
      setUpgradeSuccessShow(true)
      showToastNotification('success', {
        message: 'Your subscription has been upgraded successfully!',
      })
    }
  }, [subscriptionSuccessful])

  const {
    isLoading: isLoadingCoupon,
    isRefetching: isRefetchingCoupon,
    refetch: refetchCoupon,
  } = trpc.getCouponDetails.useQuery(
    {
      couponId: !form.getFieldState('coupon').invalid ? form.getValues('coupon') : '',
    },
    { enabled: false },
  )

  useEffect(() => {
    if (userPaymentDetails && !isLoadingPaymentDetails) {
      setAddCardOpen(false)
    }
  }, [userPaymentDetails, isLoadingPaymentDetails])

  if (isLoadingPaymentDetails) {
    return (
      <div className="flex justify-center items-center w-full h-full">
        <Loader />
      </div>
    )
  }

  if (upgradeSuccessShow) {
    return <UpgradeSuccessModal type={'upgrade'} />
  }

  return (
    <Form {...form}>
      <form
        // if the user is using existing details, just submit,
        // but if new card details is being added,
        // validate with react-hook-form
        onSubmit={userPaymentDetails && !addCardOpen ? onSubmit : form.handleSubmit(onSubmit)}
      >
        <div className={'flex flex-col gap-6'}>
          <DialogTitle>
            <span className={'text-xl lg:text-2xl text-left font-semibold'}>Purchase Seat(s)</span>
          </DialogTitle>
          <div>
            <div className={'flex flex-col md:flex-row md:justify-between gap-3 items-center'}>
              <p className={'font-bold'}>Subscription</p>
            </div>
            <div className={'flex flex-col'}>
              <div className={'flex flex-col gap-3 py-3 border-b border-dashed'}>
                <div>
                  <div className={'flex gap-3 justify-between text-sm'}>
                    <span className={'flex gap-1 items-center'}>
                      <span>CreativeOS Additional Seat</span>
                    </span>
                    <span className={'font-semibold'}>
                      ${defaultPrice} / {period === 'monthly' ? 'month' : 'year'}
                    </span>
                  </div>
                  <div className={'flex gap-3 justify-between text-xs font-light text-thememutedforeground'}>
                    <span>{period === 'monthly' ? 'Monthly' : 'Annual'} Subscription, cancel anytime.</span>
                    <span>{period === 'monthly' ? null : '$42 / mo avg.'}</span>
                  </div>
                </div>

                <div>
                  {addPromoOpen ? (
                    <div className={'flex gap-1.5'}>
                      <FormField
                        control={form.control}
                        name="coupon"
                        render={({ field }) => (
                          <FormItem className={'flex-1'}>
                            <FormControl>
                              <Input type={'text'} placeholder={'Add Promo Code'} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        onClick={() => {
                          applyPromo()
                        }}
                        variant={'secondary'}
                        size={'sm'}
                        type={'button'}
                        disabled={isLoadingCoupon || isRefetchingCoupon || form.watch('coupon') === ''}
                        loading={isLoadingCoupon || isRefetchingCoupon}
                      >
                        {isLoadingCoupon || isRefetchingCoupon ? 'Checking...' : 'Apply'}
                      </Button>
                    </div>
                  ) : appliedPromo ? (
                    <div>
                      <div className={'flex gap-3 justify-between text-sm'}>
                        <span className={'flex gap-1 items-center'}>
                          <img src={'/label.png'} alt={''} />
                          <span>{appliedPromo.name}</span>
                          <span
                            onClick={() => {
                              setAddPromoOpen(false)
                              setAppliedPromo(undefined)

                              form.setValue('coupon', '')
                              // Refetch upgrade estimate after clearing the coupon
                              // refetchUpgradeEstimate();
                            }}
                            className={'text-thememutedforeground cursor-pointer'}
                          >
                            <XCircle className={'w-4 h-4'} />
                          </span>
                        </span>
                        <span className={'font-semibold'}>
                          -
                          {appliedPromo.discountType === 'fixed_amount' && appliedPromo.currencyCode
                            ? getCurrencySymbol(appliedPromo.currencyCode)
                            : ''}
                          {appliedPromo.discountAmount
                            ? `${(appliedPromo.discountAmount / 100).toFixed(2)}`
                            : `${appliedPromo.discountPercentage}%`}{' '}
                          {appliedPromo.durationType === 'limited_period'
                            ? `/ ${appliedPromo.periodUnit}`
                            : appliedPromo.durationType}
                        </span>
                      </div>
                      <div
                        className={'flex gap-3 justify-between text-xs font-light text-thememutedforeground capitalize'}
                      >
                        <span>
                          {appliedPromo.discountType === 'fixed_amount' && appliedPromo.currencyCode
                            ? getCurrencySymbol(appliedPromo.currencyCode)
                            : ''}
                          {appliedPromo.discountAmount
                            ? `${(appliedPromo.discountAmount / 100).toFixed(2)}`
                            : `${appliedPromo.discountPercentage}%`}
                          {' OFF'}{' '}
                          {appliedPromo?.periodUnit === 'month'
                            ? 'monthly'
                            : appliedPromo?.periodUnit === 'year'
                              ? 'yearly'
                              : ''}{' '}
                          {' Price '}
                          {appliedPromo.periodUnit
                            ? `for ${appliedPromo.period} ${appliedPromo.periodUnit}`
                            : appliedPromo.durationType}
                        </span>
                        <span>
                          {appliedPromo.durationType === 'limited_period' &&
                            `For ${appliedPromo.period} ${appliedPromo.periodUnit}`}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <span
                      onClick={() => setAddPromoOpen(true)}
                      className={'text-sm underline text-themedestructive font-medium cursor-pointer'}
                    >
                      Add Promo Code
                    </span>
                  )}
                </div>

                <div className={'flex justify-between gap-3'}>
                  <p className={'text-sm'}>First Charge Date</p>
                  <p className={'text-sm font-semibold text-right'}>{firstChargeDate}</p>
                </div>

                <div className={'flex justify-between gap-3'}>
                  <p className={'text-sm'}>Billing Cycle</p>
                  <p className={'text-sm font-semibold text-right'}>Monthly</p>
                </div>
              </div>

              <div className={'flex justify-between gap-3 py-2'}>
                <p className={'text-sm'}>Due Today</p>
                <p className={'text-sm font-semibold text-right'}>{`$${defaultPrice}`}</p>
              </div>
            </div>
          </div>

          <div>
            <div className={'flex flex-col gap-6'}>
              <div className={'flex justify-between gap-3 items-center'}>
                <p className={'font-bold'}>Payment Details</p>
                {fetchingPortalSessionUrl ? (
                  <span className="font-medium text-sm leading-5 text-thememutedforeground tracking-wide	">
                    Loading...
                  </span>
                ) : addCardOpen ? (
                  <span
                    className={'underline font-medium text-sm cursor-pointer'}
                    onClick={() => setAddCardOpen(false)}
                  >
                    Cancel
                  </span>
                ) : (
                  <span
                    className={'underline font-medium text-sm cursor-pointer'}
                    // onClick={() => setAddCardOpen(true)}
                    onClick={() => openPortalSessionUrl()}
                  >
                    Add Card
                  </span>
                )}
              </div>
              {!addCardOpen && userPaymentDetails ? (
                <PaymentSourceDetails paymentSource={userPaymentDetails} />
              ) : (
                <div className={'flex flex-col gap-6'}>
                  <div className={'grid grid-cols-2 gap-3'}>
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <Label>
                            First Name <span className={'text-red-500'}>*</span>
                          </Label>
                          <FormControl>
                            <Input placeholder="First Name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <Label>
                            Last Name <span className={'text-red-500'}>*</span>
                          </Label>
                          <FormControl>
                            <Input placeholder="Last Name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <CardComponent className="field" ref={cardRef} onReady={onReady}>
                    <div>
                      <Label>
                        Card Number <span className={'text-red-500'}>*</span>
                      </Label>
                      <CardNumber placeholder="4111 1111 1111 1111" className={cn(inputStyles())} />
                    </div>
                    <div className={'grid lg:grid-cols-2 gap-3 mt-4'}>
                      <div>
                        <Label>
                          Expiration Date <span className={'text-red-500'}>*</span>
                        </Label>
                        <CardExpiry placeholder="MM / YY" className={cn(inputStyles())} />
                      </div>
                      <div>
                        <Label>
                          CVV <span className={'text-red-500'}>*</span>
                        </Label>
                        <CardCVV placeholder="CVV" className={cn(inputStyles())} />
                      </div>
                    </div>
                  </CardComponent>
                  <div>
                    <p className={'font-medium'}>Billing Details</p>
                  </div>
                  <div className={'flex flex-col gap-3'}>
                    <FormField
                      control={form.control}
                      name="billingAddress.country"
                      render={({ field }) => (
                        <FormItem>
                          <Label>
                            Country <span className={'text-red-500'}>*</span>
                          </Label>
                          <FormControl>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select country" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {countries &&
                                  countries.map((item) => (
                                    <SelectItem key={item.name} value={item.code2}>
                                      {item.name}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="billingAddress.line1"
                      render={({ field }) => (
                        <FormItem>
                          <Label>
                            Address <span className={'text-red-500'}>*</span>
                          </Label>
                          <FormControl>
                            <Input placeholder="Line 1" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="billingAddress.line2"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input placeholder="Line 2" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="billingAddress.city"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input placeholder="City" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className={'grid grid-cols-2 gap-3'}>
                      {countries &&
                      form.getValues('billingAddress.country') &&
                      getStatesByCode2(form.watch('billingAddress.country') ?? '').length < 1 ? (
                        <FormField
                          control={form.control}
                          name="billingAddress.state"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input placeholder="State" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ) : (
                        <FormField
                          control={form.control}
                          name="billingAddress.state"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Select
                                  onValueChange={field.onChange}
                                  defaultValue={field.value}
                                  disabled={!form.getValues('billingAddress.country')}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="State" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {countries &&
                                      form.getValues('billingAddress.country') &&
                                      getStatesByCode2(form.watch('billingAddress.country') ?? '').map((item) => (
                                        <SelectItem key={item.name} value={item.name}>
                                          {item.name}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                      <FormField
                        control={form.control}
                        name="billingAddress.zip"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input placeholder="Zip code" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className={'mt-6'}>
              <Button
                disabled={
                  isSubmitting ||
                  isPending ||
                  (isMonitoringSubscription && subscriptionSuccessful) ||
                  (!addCardOpen && userPaymentDetails?.status === 'expired')
                }
                type={'button'}
                onClick={userPaymentDetails && !addCardOpen ? onSubmit : form.handleSubmit(onSubmit)}
                className="w-full"
                loading={isSubmitting || (isMonitoringSubscription && subscriptionSuccessful) || isPending}
              >
                {isSubmitting || (isMonitoringSubscription && subscriptionSuccessful) || isPending
                  ? 'Processing...'
                  : 'Pay Now'}
              </Button>
              <div>
                <span className={'flex justify-center items-center gap-2 text-center mt-1.5 text-sm'}>
                  <Lock className={'w-5'} />
                  <span>Secure Checkout by Chargebee</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </form>
    </Form>
  )
}

export function UpgradeSuccessModal({
  type,
  newPlanName,
}: {
  type: 'upgrade' | 'downgrade' | 'discounted'
  newPlanName?: string
}) {
  const [enableClose, setEnableClose] = useState<boolean>(false)

  useEffect(() => {
    const enableCloseHandler = () => {
      setTimeout(() => {
        setEnableClose(true)
      }, 5000)
    }
    enableCloseHandler()
  }, [])

  return (
    <div>
      <div className={'flex flex-col items-center gap-2'}>
        <img alt={'CreativeOS'} src={'/images/logo-full-dark_mode.png'} className={'h-7  dark:block hidden'} />
        <img alt={'CreativeOS'} src={'/images/logo-full-light_mode.png'} className={'h-7 dark:hidden block'} />
        <h4 className={'text-2xl text-center text-themeforeground font-semibold '}>
          {type === 'downgrade'
            ? 'Downgrade Scheduled'
            : type === 'discounted'
              ? 'Discount Applied'
              : 'You’re all set!'}
        </h4>
        <p className={'text-thememutedforeground text-lg text-center'}>{getSubscriptionMessage(type, newPlanName)}</p>
      </div>
      <div className={'mt-6'}>
        <Button
          onClick={() => {
            window.location.href = '/feeds/templates'
          }}
          type={'button'}
          className="w-full"
          disabled={!enableClose}
          loading={!enableClose}
        >
          {'Start Creating'}
        </Button>
      </div>
    </div>
  )
}

function getSubscriptionMessage(type: string, newPlanName?: string): string {
  if (type === 'downgrade') {
    return `Your subscription will be downgraded to ${newPlanName} starting from your next renewal.`
  }

  if (type === 'discounted') {
    return 'The discount will be applied to your plan starting from your next renewal.'
  }

  return 'We’ve received your payment, go start creating!'
}
